import { Price } from "../services/searchService";

// Night window and state-based GST split mirror FusionCab's admin-side
// booking logic (fusion_admindash CreateBookingPage.tsx / backend
// calculationUtils.js) - 22:00-06:00 pickup counts as night, and GST is
// CGST+SGST 2.5% each for Telangana-origin trips, IGST 5% otherwise.
const NIGHT_START_HOUR = 22;
const NIGHT_END_HOUR = 6;
const CGST_SGST_RATE = 0.025;
const IGST_RATE = 0.05;
const HOME_STATE = "telangana";

export interface FareBreakdown {
  distanceKm: number;
  includedKm: number;
  billedKm: number;
  extraKm: number;
  isNight: boolean;
  baseFare: number;
  extraFare: number;
  nightCharges: number;
  tollCharges: number;
  parkingCharges: number;
  driverCharges: number;
  waitingCharges: number;
  stateTax: number;
  greenTax: number;
  totalFareBeforeTax: number;
  sgst: number;
  cgst: number;
  igst: number;
  totalFare: number;
}

export function isNightTime(pickupTime: string): boolean {
  if (!pickupTime) return false;
  const hours = parseInt(pickupTime.split(":")[0], 10);
  if (isNaN(hours)) return false;
  return hours >= NIGHT_START_HOUR || hours < NIGHT_END_HOUR;
}

// Inclusive calendar day count between two "YYYY-MM-DD" dates - e.g. pickup
// Monday, return Thursday = 4 days (Mon, Tue, Wed, Thu). Every per-day
// figure (minimum km, driver allowance, night charges) is billed against
// this, not the number of nights. Single-day trip types (One-Way, Airport,
// Hourly) never have a return date, so this always resolves to 1 for them.
function inclusiveDayCount(startDate?: string, endDate?: string): number {
  if (!startDate || !endDate) return 1;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffDays = Math.round((end.getTime() - start.getTime()) / 86400000);
  return Math.max(1, diffDays + 1);
}

// GST is charged only on the fare/service components (base fare, driver
// allowance, night charges) - toll, parking, state/interstate tax and green
// tax are statutory/pass-through charges and stay untaxed, added on top of
// totalFareBeforeTax as-is.
function computeGst(taxableAmount: number, totalFareBeforeTax: number, sourceState?: string) {
  const isHomeState = (sourceState || "").trim().toLowerCase() === HOME_STATE;
  const sgst = isHomeState ? taxableAmount * CGST_SGST_RATE : 0;
  const cgst = isHomeState ? taxableAmount * CGST_SGST_RATE : 0;
  const igst = isHomeState ? 0 : taxableAmount * IGST_RATE;
  return { sgst, cgst, igst, totalFare: totalFareBeforeTax + sgst + cgst + igst };
}

/**
 * Shared pricing core for both single-leg and Multi-City fares, once each
 * has worked out its own `billedKm`/`night`/`days`:
 *
 * - Base fare is billed against `billedKm` - whichever is higher of the
 *   per-day minimum x days, or the actual trip distance (see callers) - at a
 *   per-km rate. During the night window (22:00-06:00), that rate is
 *   price.night_base_fare instead of price.fixed_fare - not an addition on
 *   top, a replacement of the whole trip's per-km rate. `billedKm` is a
 *   pricing input only, never shown to the customer as-is - the
 *   customer-facing "included km" is the separate, smaller `includedKm`
 *   (see callers), which is also what's later used as the floor for extra-km
 *   billing at trip completion (see fusion_backend's
 *   calculateBookingCompletionFare) - never as part of the up-front quote.
 * - Driver allowance and the Plan's flat night-charges add-on are both
 *   per-day figures, scaled by the same day count as the km minimum.
 * - Waiting charges are never part of the quote - they're only known once
 *   the driver reports actual waiting minutes at trip completion.
 * - Toll, parking, state tax and green tax are flat, untaxed pass-through
 *   charges - shown to the customer as exclusions, not part of what's
 *   "included" in the quoted price.
 */
function computeCorePricing(
  price: Price,
  billedKm: number,
  night: boolean,
  days: number,
  sourceState?: string
) {
  const plan = price.Plan;

  const baseFare = (night ? price.night_base_fare : price.fixed_fare) * billedKm;
  const nightCharges = night && plan.is_night ? plan.night_charges * days : 0;
  const driverCharges = plan.is_driver ? plan.driver_charges * days : 0;

  const tollCharges = plan.is_toll ? plan.toll_charges : 0;
  const parkingCharges = plan.is_parking ? plan.parking_charges : 0;
  const stateTax = plan.is_tax ? plan.state_tax : 0;
  const greenTax = plan.is_tax ? plan.green_tax : 0;
  const waitingCharges = 0;

  const taxableAmount = baseFare + nightCharges + driverCharges;
  const totalFareBeforeTax =
    baseFare + nightCharges + driverCharges + tollCharges + parkingCharges + stateTax + greenTax;

  const { sgst, cgst, igst, totalFare } = computeGst(taxableAmount, totalFareBeforeTax, sourceState);

  return {
    baseFare,
    nightCharges,
    driverCharges,
    tollCharges,
    parkingCharges,
    waitingCharges,
    stateTax,
    greenTax,
    totalFareBeforeTax,
    sgst,
    cgst,
    igst,
    totalFare,
  };
}

export function calculateFare(
  price: Price,
  distanceKm: number | undefined,
  pickupTime: string,
  sourceState?: string,
  departureDate?: string,
  returnDate?: string
): FareBreakdown {
  const plan = price.Plan;
  const days = inclusiveDayCount(departureDate, returnDate);
  const minKm = plan.distance * days;
  const effectiveDistanceKm = distanceKm ?? minKm;
  const billedKm = Math.max(minKm, effectiveDistanceKm);
  // Customer-facing "included" km is the calculated trip distance only,
  // rounded up - never the plan's base/minimum km, even when the base is
  // higher. The base is a pricing floor only (see billedKm above); it's
  // never shown to the customer.
  const includedKm = Math.ceil(effectiveDistanceKm);
  const night = isNightTime(pickupTime);

  const core = computeCorePricing(price, billedKm, night, days, sourceState);

  return {
    distanceKm: effectiveDistanceKm,
    includedKm,
    billedKm,
    extraKm: 0,
    isNight: night,
    extraFare: 0,
    ...core,
  };
}

// A single leg of a Multi-City itinerary's fare calculation - stops carry
// no per-leg time (see types/stop.ts), so this is just a distance.
export interface FareLeg {
  distanceKm: number;
}

/**
 * Multi-City fare: total distance is every forward leg (source->stop1->...
 * ->destination) PLUS the return leg back to source. `plan.distance` is the
 * per-day minimum km guarantee - the billed/included km is whichever is
 * higher: that per-day minimum times the number of days, or the actual
 * itinerary distance. Night charges are decided by `pickupTime` alone (same
 * as every other trip type), applied at most once for the whole itinerary,
 * since individual stops/legs don't carry their own time. The return leg's
 * distance is a pricing input only - never surfaced as its own line item,
 * it just contributes to the same aggregate totals below.
 */
export function calculateMultiCityFare(
  price: Price,
  legs: FareLeg[],
  pickupTime: string,
  sourceState?: string,
  departureDate?: string,
  returnDate?: string
): FareBreakdown {
  const plan = price.Plan;
  const days = inclusiveDayCount(departureDate, returnDate);
  const minItineraryKm = plan.distance * days;
  const totalDistanceKm = legs.reduce((sum, leg) => sum + leg.distanceKm, 0);
  const effectiveDistanceKm = totalDistanceKm || minItineraryKm;
  const billedKm = Math.max(minItineraryKm, effectiveDistanceKm);
  // See calculateFare - customer-facing "included" km is the calculated
  // itinerary distance only, rounded up, never the per-day minimum.
  const includedKm = Math.ceil(effectiveDistanceKm);
  const night = isNightTime(pickupTime);

  const core = computeCorePricing(price, billedKm, night, days, sourceState);

  return {
    distanceKm: effectiveDistanceKm,
    includedKm,
    billedKm,
    extraKm: 0,
    isNight: night,
    extraFare: 0,
    ...core,
  };
}
